/**
 * Dependency graph for incremental measurement recomputation.
 *
 * Tracks which measurements are affected when a keypoint moves
 * or a diameter is edited, enabling selective recomputation.
 */

import { MeasurementType, type ISizingTemplate, type IMeasurementDefinition } from '../templates/types';

export interface DependencyGraph {
    /** Measurement IDs that reference a given keypoint ID. */
    readonly byKeypoint: ReadonlyMap<string, readonly string[]>;
    /** Measurement IDs that depend on other measurement IDs (derived). */
    readonly byMeasurement: ReadonlyMap<string, readonly string[]>;
}

/**
 * Build a dependency graph from a sizing template.
 *
 * Used to determine which measurements need recomputation
 * when a keypoint moves or a measurement value changes.
 */
export function buildDependencyGraph(template: ISizingTemplate): DependencyGraph {
    const byKeypoint = new Map<string, string[]>();
    const byMeasurement = new Map<string, string[]>();

    function addKeypointDep(keypointId: string, measurementId: string): void {
        let list = byKeypoint.get(keypointId);
        if (!list) {
            list = [];
            byKeypoint.set(keypointId, list);
        }
        list.push(measurementId);
    }

    function addMeasurementDep(depMeasurementId: string, measurementId: string): void {
        let list = byMeasurement.get(depMeasurementId);
        if (!list) {
            list = [];
            byMeasurement.set(depMeasurementId, list);
        }
        list.push(measurementId);
    }

    for (const def of template.measurements) {
        registerDependencies(def, addKeypointDep, addMeasurementDep);
    }

    return { byKeypoint, byMeasurement };
}

function registerDependencies(
    def: IMeasurementDefinition,
    addKeypointDep: (kpId: string, mId: string) => void,
    addMeasurementDep: (depId: string, mId: string) => void
): void {
    switch (def.type) {
        case MeasurementType.LENGTH:
            addKeypointDep(def.fromKeypointId, def.id);
            addKeypointDep(def.toKeypointId, def.id);
            break;

        case MeasurementType.DIAMETER:
            addKeypointDep(def.keypointId, def.id);
            break;

        case MeasurementType.OFFSET_DIAMETER:
            addKeypointDep(def.keypointId, def.id);
            break;

        case MeasurementType.MIN_MAX_DIAMETER:
            addKeypointDep(def.fromKeypointId, def.id);
            addKeypointDep(def.toKeypointId, def.id);
            break;

        case MeasurementType.ANGLE:
            for (const kpId of def.keypointIds) {
                addKeypointDep(kpId, def.id);
            }
            break;

        case MeasurementType.DERIVED:
            for (const depId of def.dependencies) {
                addMeasurementDep(depId, def.id);
            }
            break;
    }
}

/**
 * Given a set of changed keypoint IDs, collect all measurement IDs that need recomputation,
 * including transitive derived dependencies.
 */
export function getAffectedMeasurements(
    changedKeypointIds: readonly string[],
    graph: DependencyGraph
): Set<string> {
    const affected = new Set<string>();

    // Direct dependencies from keypoints
    for (const kpId of changedKeypointIds) {
        const deps = graph.byKeypoint.get(kpId);
        if (deps) {
            for (const id of deps) affected.add(id);
        }
    }

    // Transitive derived dependencies (BFS)
    const queue = [...affected];
    while (queue.length > 0) {
        const mId = queue.shift()!;
        const derivedDeps = graph.byMeasurement.get(mId);
        if (derivedDeps) {
            for (const id of derivedDeps) {
                if (!affected.has(id)) {
                    affected.add(id);
                    queue.push(id);
                }
            }
        }
    }

    return affected;
}

/**
 * Collect all measurement IDs potentially affected by a diameter edit.
 *
 * Diameter edits can affect: diameter measurements, offset diameters, min/max diameters,
 * and any derived measurements that depend on them.
 */
export function getAffectedByDiameterEdit(
    template: ISizingTemplate,
    graph: DependencyGraph,
    customOffsets: readonly IMeasurementDefinition[] = []
): Set<string> {
    const affected = new Set<string>();

    // All diameter-related measurements could be affected (including custom offsets)
    for (const def of [...template.measurements, ...customOffsets]) {
        if (
            def.type === MeasurementType.DIAMETER ||
            def.type === MeasurementType.OFFSET_DIAMETER ||
            def.type === MeasurementType.MIN_MAX_DIAMETER
        ) {
            affected.add(def.id);
        }
    }

    // Transitive derived dependencies
    const queue = [...affected];
    while (queue.length > 0) {
        const mId = queue.shift()!;
        const derivedDeps = graph.byMeasurement.get(mId);
        if (derivedDeps) {
            for (const id of derivedDeps) {
                if (!affected.has(id)) {
                    affected.add(id);
                    queue.push(id);
                }
            }
        }
    }

    return affected;
}
